import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import requests from './api/client';
import { resolveAssetUrl } from './api/assets';
import { useSettings } from './Settings';
import type { BrandIdentity } from './types/api';
import type { BrandState } from './types/brand';

/**
 * Identidade da marca corrente.
 *
 * **A marca é o hostname, ponto.** Toda chamada sai com o header
 * `x-caller-url` (ver `src/api/client.ts`); o middleware traduz esse hostname
 * em token de tenant e `GET /recarga-facil/marca` devolve a identidade da
 * própria marca, sem parâmetro nenhum. É o mesmo modelo do
 * `link-venda-direta` e do `webpresence`: um domínio por marca, sem prefixo
 * `/{marca}` na URL e sem descobrir a marca pela linha digitada.
 *
 * Consequências que valem registrar:
 *
 * - **Hostname não cadastrado no `ACCESS_KEY` do middleware ⇒ tela de
 *   indisponível.** A busca falha, `error` é preenchido e o `AppRoutes`
 *   renderiza `NotFound`. A identidade padrão embutida abaixo é só a base do
 *   tema enquanto a resposta não chega — nunca um resultado válido. Sem isso o
 *   domínio da marca abriria exibindo a identidade Contel, que é o pior modo
 *   de falha possível num produto white-label.
 * - **`DEFAULT_IDENTITY` continua existindo** porque a API responde com o
 *   bloco padrão Contel (verde `#02EA75`, SAC 0800 900 6060) para token que
 *   não é do tipo MARCA — é o caso do `localhost` em desenvolvimento.
 *
 * Os valores visuais são aplicados como CSS custom properties no `<html>`,
 * então qualquer componente pode usar `var(--color-primary, …)` sem saber de
 * onde a cor veio.
 */

/** Verde Recarga Fácil — o default histórico do bloco padrão Contel. */
export const DEFAULT_PRIMARY_COLOR = '#02EA75';

/** Logo Contel usado enquanto a identidade da marca não chegou. */
export const DEFAULT_LOGO_URL =
  'https://tecnologia.conteltelecom.com.br/img/Logo-Contel-Verde-sem-fundo.png';

const DEFAULT_IDENTITY: BrandIdentity = {
  nomeFantasia: 'Recarga Fácil',
  logoUrl: DEFAULT_LOGO_URL,
  primaryColor: DEFAULT_PRIMARY_COLOR,
  sac: '0800 900 6060',
  whatsapp: '558009006060',
  site: 'www.conteltelecom.com.br',
  playStoreUrl: null,
  appStoreUrl: null,
  active: true,
};

/** Garante o `#` inicial, como o helper `$fixColor` das views Blade. */
export function normalizeColor(color: string | null | undefined, fallback: string): string {
  if (!color) return fallback;
  return color.startsWith('#') ? color : `#${color}`;
}

/** Deriva um tom mais escuro para estados de hover, sem depender do ERP. */
function darken(hex: string, amount = 0.14): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!match) return hex;
  const value = parseInt(match[1], 16);
  const channels = [(value >> 16) & 255, (value >> 8) & 255, value & 255].map((channel) =>
    Math.max(0, Math.round(channel * (1 - amount)))
  );
  return `#${channels.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

function applyBrandCssVariables(identity: BrandIdentity): void {
  const primary = normalizeColor(identity.primaryColor, DEFAULT_PRIMARY_COLOR);
  const root = document.documentElement;
  root.style.setProperty('--color-primary', primary);
  root.style.setProperty('--color-primary-dark', darken(primary));
  root.style.setProperty('--color-brand-header', primary);
}

interface BrandContextValue extends BrandState {
  /**
   * `true` quando o ERP está com o Recarga Fácil desligado para esta marca
   * (`st_recarga_facil = 0`). A aplicação renderiza a tela de indisponível —
   * é o mesmo bloqueio que o `SetBrandTheme` fazia devolvendo
   * `service_unavailable`.
   */
  isBlocked: boolean;
}

const BrandContext = createContext<BrandContextValue | null>(null);

export function useBrand(): BrandContextValue {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand precisa estar dentro de <BrandProvider>');
  }
  return context;
}

interface BrandProviderProps {
  children: React.ReactNode;
}

export function BrandProvider({ children }: BrandProviderProps) {
  const { settings, loading: settingsLoading } = useSettings();

  const [state, setState] = useState<BrandState>({
    identity: DEFAULT_IDENTITY,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (settingsLoading) return;

    let cancelled = false;

    setState((current) => ({ ...current, loading: true, error: null }));

    requests
      .brandIdentity()
      .then((identity) => {
        if (cancelled) return;
        setState({
          // O settings.json da marca sempre vence sobre a API: é o override manual.
          identity: {
            ...identity,
            nomeFantasia: settings.brand?.name || identity.nomeFantasia,
            // Mesmo contrato do favIcon: caminho relativo é relativo à pasta
            // da marca, URL absoluta passa direto (`resolveAssetUrl`).
            logoUrl: settings.brand?.logo
              ? resolveAssetUrl(settings.brand.logo)
              : identity.logoUrl,
            primaryColor: settings.brand?.primaryColor || identity.primaryColor,
            sac: settings.brand?.sac || identity.sac,
            site: settings.brand?.website || identity.site,
            whatsapp: settings.brand?.whatsapp || identity.whatsapp,
          },
          loading: false,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Com domínio próprio não existe "falha tolerável": o hostname É a
        // marca. Falhar aqui (hostname fora do `ACCESS_KEY`, marca inexistente,
        // API fora do ar) precisa virar tela de indisponível, nunca a
        // identidade Contel no domínio de outra empresa.
        setState({
          identity: DEFAULT_IDENTITY,
          loading: false,
          error: error instanceof Error ? error.message : 'Falha ao carregar a marca',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [settingsLoading, settings.brand]);

  useEffect(() => {
    applyBrandCssVariables(state.identity);
  }, [state.identity]);

  const value = useMemo<BrandContextValue>(
    () => ({
      ...state,
      isBlocked: state.identity.active === false,
    }),
    [state]
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
